import { useState } from 'react';
import { Copy, ExternalLink, Link2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@/components/ui';
import { getStorefrontOrigin } from '@/features/cms/preview/sfOrigin';

/**
 * QA-331 상품 URL(단축) — SNS 등 공개용. 미리보기 URL(검수용, 만료 있음)과 별개로 만료가 없다.
 *
 * catalog 가 상품 등록 시 발급한 단축 코드(commerceMeta.shortCode, 이관 상품은 백필) 로 FE `/s/{code}` 를 만든다.
 * FE 는 코드를 상품으로 바꿔 `/products/{id}` 로 보낸다(FE 공유 버튼과 같은 링크).
 *
 * 열림 기준(catalog #45, 고도몰 노출 기준): 노출 ON + 노출 기간 안 + 보관 아님이면 판매 상태와 상관없이 열리고,
 * 구매만 서버가 막는다(order #33 — 판매중이 아니거나 판매기간 밖이면 장바구니 400). 그래서 팝업은
 * - 링크가 안 열리는 경우(노출 OFF·노출 기간 밖)만 경고하고,
 * - 판매중이 아니거나 판매기간 밖이면 "페이지는 열리고 구매는 불가"로 안내한다.
 * 보관(ARCHIVED)은 BO 상세에서 판매종료(SALE_ENDED)와 구분되지 않아 여기서 판정하지 않는다.
 */

export interface ShareableProduct {
  status?: string | null;
  displayed?: boolean | null;
  salesStartAt?: string | null;
  salesEndAt?: string | null;
  commerceMeta?: {
    shortCode?: string | null;
    displayStartAt?: string | null;
    displayEndAt?: string | null;
    visibleMallCodes?: string[] | null;
  } | null;
}

/**
 * QA-491 — 몰별 URL. FE 는 몰을 경로 프리픽스로 나눈다(국내몰=루트, 영문 /us, 일문 /jp, 중문 /cn —
 * storefront localePath). `/us/s/{code}` 는 영문몰 PDP 로 간다. 단축 코드는 상품당 하나라 몰을 나중에
 * 추가해도 처음 만든 국내몰 URL 은 그대로이고, 추가된 몰의 URL 만 늘어난다.
 */
const SHARE_MALLS = [
  { mall: 'ko-KR', prefix: '', label: '국내몰' },
  { mall: 'en-US', prefix: '/us', label: '영문몰' },
  { mall: 'ja-JP', prefix: '/jp', label: '일문몰' },
  { mall: 'zh-CN', prefix: '/cn', label: '중문몰' },
] as const;

export type ShareMall = (typeof SHARE_MALLS)[number]['mall'];

export const productShortUrl = (shortCode: string, mall: ShareMall = 'ko-KR'): string => {
  const prefix = SHARE_MALLS.find((m) => m.mall === mall)?.prefix ?? '';
  return `${getStorefrontOrigin()}${prefix}/s/${encodeURIComponent(shortCode)}`;
};

/** 노출 국가(몰) 체크에 따른 몰별 URL. visibleMallCodes 가 없거나 비면 전체 노출(4몰). */
export const productShortUrlsByMall = (
  shortCode: string,
  visibleMallCodes: ReadonlyArray<string> | null | undefined,
): { mall: ShareMall; label: string; url: string }[] => {
  const all = !visibleMallCodes || visibleMallCodes.length === 0;
  return SHARE_MALLS.filter((m) => all || visibleMallCodes.includes(m.mall)).map((m) => ({
    mall: m.mall,
    label: m.label,
    url: productShortUrl(shortCode, m.mall),
  }));
};

export type ShareBlockedReason = 'hidden' | 'beforeDisplay' | 'afterDisplay';

const REASON_FALLBACK: Record<ShareBlockedReason, string> = {
  hidden: '노출이 꺼져 있어',
  beforeDisplay: '노출 시작 전이라',
  afterDisplay: '노출 기간이 끝나',
};

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 링크가 지금 열리지 않는 이유(없으면 null) — catalog 열림 기준(노출 ON + 노출 기간 안). */
export const shareBlockedReason = (p: ShareableProduct, now: Date = new Date()): ShareBlockedReason | null => {
  if (p.displayed === false) return 'hidden';
  const from = p.commerceMeta?.displayStartAt ? new Date(p.commerceMeta.displayStartAt) : null;
  const to = p.commerceMeta?.displayEndAt ? new Date(p.commerceMeta.displayEndAt) : null;
  if (from && !Number.isNaN(from.getTime()) && from > now) return 'beforeDisplay';
  if (to && !Number.isNaN(to.getTime()) && to < now) return 'afterDisplay';
  return null;
};

export type PurchaseBlockedReason = 'notOnSale' | 'beforeSales' | 'afterSales';

/** 링크는 열리지만 구매가 막히는 이유(없으면 null) — order 장바구니 게이트와 같은 기준. */
export const purchaseBlockedReason = (p: ShareableProduct, now: Date = new Date()): PurchaseBlockedReason | null => {
  if (p.status !== 'ON_SALE') return 'notOnSale';
  const start = toDate(p.salesStartAt);
  const end = toDate(p.salesEndAt);
  if (start && start > now) return 'beforeSales';
  if (end && end < now) return 'afterSales';
  return null;
};

const formatKst = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
};

export const ProductShareUrlButton = ({ product }: { product: ShareableProduct }) => {
  const { t } = useTranslation('products');
  const [open, setOpen] = useState(false);
  const shortCode = product.commerceMeta?.shortCode?.trim() || null;
  const urls = shortCode ? productShortUrlsByMall(shortCode, product.commerceMeta?.visibleMallCodes) : [];
  const blocked = shareBlockedReason(product);
  const purchase = blocked ? null : purchaseBlockedReason(product);
  const salesStart = formatKst(product.salesStartAt);

  const copy = async (url: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success(t('products.shareUrl.copied', '상품 URL을 클립보드에 복사했습니다.'));
        return;
      }
    } catch {
      // 비보안 컨텍스트 등 — 아래 안내로.
    }
    toast.error(t('products.shareUrl.copyFailed', '복사에 실패했습니다. URL을 직접 선택해 복사하세요.'));
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!shortCode}
        title={shortCode ? undefined : t('products.shareUrl.noCode', '단축 코드가 아직 없습니다. 잠시 후 다시 시도하세요.')}
        onClick={() => {
          setOpen(true);
          // 몰이 하나뿐이면 바로 복사, 여럿이면 팝업에서 몰별로 복사한다.
          if (urls.length === 1) void copy(urls[0]!.url);
        }}
        data-testid="product-share-url"
      >
        <Link2 size={14} aria-hidden="true" className="mr-1.5" />
        {t('products.shareUrl.button', '상품 URL')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.shareUrl.title', '상품 URL')}</DialogTitle>
            <DialogDescription>
              {t('products.shareUrl.desc', 'SNS 등에 올릴 수 있는 상품 단축 링크입니다. 만료되지 않습니다.')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {urls.map(({ mall, label, url }) => (
              <div key={mall} className="flex flex-col gap-1" data-testid={`product-share-url-row-${mall}`}>
                <span className="text-[12px] font-medium" style={{ color: 'var(--content-strong)' }}>
                  {t(`products.shareUrl.mall.${mall}`, label)}
                </span>
                <div className="flex items-center gap-2">
                  <Input value={url} readOnly aria-label={`${t('products.shareUrl.title', '상품 URL')} ${label}`} className="font-mono text-[12px]" data-testid={`product-share-url-value-${mall}`} />
                  <Button type="button" variant="outline" size="sm" onClick={() => void copy(url)} aria-label={`${label} ${t('products.shareUrl.copy', '복사')}`}>
                    <Copy size={14} aria-hidden="true" />
                  </Button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${label} ${t('products.shareUrl.open', '새 탭에서 열기')}`}
                    title={t('products.shareUrl.open', '새 탭에서 열기')}
                    className="inline-flex shrink-0 items-center"
                    style={{ color: 'var(--accent-default)' }}
                  >
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
            {blocked ? (
              <p role="alert" className="rounded-md p-3 text-[13px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }} data-testid="product-share-url-blocked">
                {t('products.shareUrl.blocked', {
                  defaultValue: '지금은 {{reason}} 링크를 열면 "상품을 찾을 수 없음"이 나옵니다. 노출을 켜고 노출 기간 안이 되면 같은 링크로 열립니다.',
                  reason: t(`products.shareUrl.reason.${blocked}`, REASON_FALLBACK[blocked]),
                })}
              </p>
            ) : purchase ? (
              <p className="rounded-md p-3 text-[13px]" style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }} data-testid="product-share-url-purchase-blocked">
                {purchase === 'beforeSales' && salesStart
                  ? t('products.shareUrl.salesLater', {
                      defaultValue: '상품 페이지는 지금 열리고, 구매는 판매 시작({{at}}) 후에 됩니다.',
                      at: salesStart,
                    })
                  : purchase === 'afterSales'
                    ? t('products.shareUrl.salesEnded', '상품 페이지는 열리지만, 판매기간이 끝나 구매할 수 없습니다.')
                    : t('products.shareUrl.notOnSale', '상품 페이지는 열리지만, 판매중이 아니라 구매할 수 없습니다. 판매중으로 바꾸면 구매할 수 있습니다.')}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('products.shareUrl.close', '닫기')}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
